import React, { forwardRef, useMemo } from 'react';
import MetricsAnchor from './MetricsAnchor';
import { useSelector } from 'react-redux';
import { getTranslationsByCountry, selectMetadataKeyByRegion } from 'reducers/selectors/metadata';
import { converter } from 'helpers/converter';
import { localize } from 'utils/apa/transformations'
import { getRegion } from 'utils/Region'
import { splitAt } from 'ramda';
import { openChatService } from './chatService';

import './styles.scss';

const FOOTER_APA_KEY = 'footer_params';
const FOOTER_ADS_APA_KEY = 'footer_params_ads';
const TWITTER_METADATA_ICON = 'fa fa-twitter';
const TWITTER_UNICODE_ICON = '𝕏';

const separateFooterLinks = (links = []) => {
  const splitIndex = Math.floor(links.length / 2);
  const [left, right] = splitAt(splitIndex, links);

  return { left, right };
}

const localizeFooterMetadata = (metadata = {}, region = '') => ({
  links: localize(metadata?.links, region)?.link || [],
  social: localize(metadata?.socials, region)?.social || [],
  contact: localize(metadata?.contact, region) || {},
  chat: localize(metadata?.chat, region) || {},
})

const Footer = ({ isVisible, isAdsEnabled }, ref) => {
  const FOOTER_KEY = isAdsEnabled ? FOOTER_ADS_APA_KEY : FOOTER_APA_KEY;

  const translations = useSelector(getTranslationsByCountry);
  const footerMetadata = useSelector(selectMetadataKeyByRegion(FOOTER_KEY));

  const { links, social, chat, contact } = isAdsEnabled
    ? footerMetadata
    : localizeFooterMetadata(footerMetadata, getRegion())

  const separatedLinks = useMemo(() => separateFooterLinks(links));
  const isFooterVisible = !isVisible ? 'footer_web--hidden' : '';

  const handleOpenChat = (e) => {
    e.preventDefault();

    openChatService({
      chatUrl: translations?.footer_chat_url,
      chatSubject: getTranslation(chat?.text),
    });
  }

  const getTranslation = key => translations?.[key] || '';

  const handleSocialIcon = (fontAwesomeClassname) => {
    if (fontAwesomeClassname === TWITTER_METADATA_ICON) {
      return <i className='fa custom-twitter-unicode'>
        {TWITTER_UNICODE_ICON}
      </i>;
    }

    return <i className={fontAwesomeClassname} />;
  }

  return (
    <footer
      className={`${isFooterVisible} container-false footer_web--footer navbar navbar-inverse padding-1`}
      ref={ref}
    >
      <div className="container2">
        <div className="footer-web hidden-xs">

          <div className="col-lg-6 hidden-md hidden-sm row">
            <ul className="list-inline">
              <li>
                <p
                  className="visible-lg text-left"
                  dangerouslySetInnerHTML={{
                    __html: converter.convertUnicode(getTranslation(contact?.text))
                  }}
                />
              </li>

              {translations?.footer_chat_url &&
                <li>
                  <a href='#' onClick={handleOpenChat}>
                    <span>
                      { getTranslation(chat?.text) }
                    </span>
                  </a>
                </li>
              }
            </ul>

            <ul className="links-footer list-inline">
              {separatedLinks?.left?.map(item =>
                <li key={item?.name}>
                  <MetricsAnchor linkItem={item} data-metricas-macrolab="txt_site" className="metricas-event" />
                  <i className="fa fa-angle-right pull-right hidden-md hidden-lg hidden-sm" />
                </li>
              )}
            </ul>
          </div>

          <div className="col-lg-5 col-lg-offset-1 hidden-md hidden-sm sociales row">
            <ul className="list-inline pull-right">
              {social?.map(item =>
                <li key={item?.url}>
                  <MetricsAnchor linkItem={item} data-metricas-macrolab="bot_site" className="social-ico metricas-event">
                    {handleSocialIcon(item?.class)}
                  </MetricsAnchor>
                </li>
              )}
            </ul>
          </div>

          <div className="col-lg-6 sociales hidden-md hidden-sm row">
            <ul className="links-footer list-inline pull-right">
              {separatedLinks?.right?.map(item =>
                <li key={item?.name}>
                  <MetricsAnchor linkItem={item} data-metricas-macrolab="txt_site" className="metricas-event" />
                  <i className="fa fa-angle-right pull-right hidden-md hidden-lg hidden-sm" />
                </li>
              )}
            </ul>
          </div>

          <div className="col-md-8 col-sm-9 visible-md visible-sm row">
            <ul className="pull-left list-inline">
              <li>
                <p
                  className="text-left"
                  dangerouslySetInnerHTML={{
                    __html: converter.convertUnicode(getTranslation(contact?.text))
                  }}
                />
              </li>

              {translations?.footer_chat_url &&
                <li>
                  <a href='#' onClick={handleOpenChat}>
                    <span>
                      { getTranslation(chat?.text) }
                    </span>
                  </a>
                </li>
              }
            </ul>

            <ul className="links-footer list-inline pull-left">
              {links?.map(item =>
                <li key={item?.name}>
                  <MetricsAnchor linkItem={item} data-metricas-macrolab="txt_site" className="metricas-event" />
                  <i className="fa fa-angle-right pull-right hidden-md hidden-lg hidden-sm" />
                </li>
              )}
            </ul>
          </div>

          <div className="col-md-4 col-sm-3 visible-md visible-sm sociales row">
            <ul className="list-inline pull-right">
              {social?.map(item =>
                <li key={item?.url}>
                  <MetricsAnchor linkItem={item} data-metricas-macrolab="bot_site" className="social-ico metricas-event">
                    {handleSocialIcon(item?.class)}
                  </MetricsAnchor>
                </li>
              )}
            </ul>
          </div>
        </div>

        <div className="footer-mobile row visible-xs">
          <div className="col-xs-12">
            <ul className="links-footer list-inline pull-left visible-xs">
              {links?.map(item =>
                <li key={item?.name}>
                  <MetricsAnchor linkItem={item} data-metricas-macrolab="txt_site" className="metricas-event">
                    <i className="fa fa-angle-right pull-right" />
                  </MetricsAnchor>
                </li>
              )}
            </ul>

            <div className="col-xs-12 sociales text-center-sm">
              <ul className="list-inline">
                {social?.map(item =>
                  <li key={item?.url}>
                    <MetricsAnchor linkItem={item} data-metricas-macrolab="bot_site" className="social-ico metricas-event">
                      {handleSocialIcon(item?.class)}
                    </MetricsAnchor>
                  </li>
                )}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default forwardRef(Footer);
